/*
  This example requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    theme: {
      extend: {
        gridTemplateRows: {
          '[auto,auto,1fr]': 'auto auto 1fr',
        },
      },
    },
    plugins: [
      // ...
      require('@tailwindcss/forms'),
      require('@tailwindcss/aspect-ratio'),
    ],
  }
  ```
*/

import { StarIcon } from '@heroicons/react/20/solid'

import { Link } from '@remix-run/react'
import lawnbed from './wyatt-bed-by-lawn.jpg'
import patiobed from './wyatt-bed-with-patio.jpg'
import wallbed from './wyatt-bed-with-wall.jpg'
import driveway from './wyatt-driveway.jpg'
import lawn from './wyatt-lawn.jpg'

const product = {
	name: 'Wyatt Conklin Lawn Maintenance',
	href: 'https://www.wclawnmaintenance.com/',
	images: [
		{
			src: driveway,
			alt: 'A landscaped driveway in a heavily wooded area.',
		},
		{
			src: lawnbed,
			alt: 'A landscaped garden bed next to a house.',
		},
		{
			src: wallbed,
			alt: 'A landscaped garden bed next to a house. There is also a retaining wall.',
		},
		{
			src: patiobed,
			alt: 'A stamped concrete patio next to a front door. There is also a landscaped garden bed.',
		},
		{
			src: lawn,
			alt: 'A flower garden with a lawn behind. The lawn is mowed with straight lines.',
		},
	],
	description:
		'When I started mowing lawns after high school, I thought of it as a way to enjoy being outside and hopefully make some money to help pay for college. I quickly realized the passion I had not only for the work I was doing, but for controlling my own destiny in life as a small business owner. Over the past 9 years of business, we have learned that serving our community with honesty and respect is the best way to sustain and grow a healthy business. We take great pride in the fact that 80% of our business comes from right here in Bloomfield. Spring, Summer, Winter or Fall, we will be here 365 days a year to serve our local businesses and residents that have continued to support us along the way.',
}
const reviews = {
	featured: [
		{
			id: 1,
			rating: 5,
			content: `
        <p>Wyatt and his team are as professional as they come. His attention to detail is unmatched and I would highly recommend them. They are some of the best in the business!!!</p>
      `,
			author: 'Nick R.',
		},
		{
			id: 2,
			rating: 5,
			content: `
        <p>I HIGHLY recommend Wyatt Conklin for lawn work! Both jobs I've hired him for have been done quickly and beautifully at a very reasonable price.</p>
      `,
			author: 'Heather K.',
		},
		{
			id: 3,
			rating: 5,
			content: `
        <p>Wyatt Conklin's team has been wonderful for us. They care AND they communicate so all parties are aligned around the objects of our project. There were a number of moving parts and Wyatt came through with flying colors. We'll be using their services around our landscaping in the future.</p>
      `,
			author: 'Ethan B.',
		},
		{
			id: 4,
			rating: 5,
			content: `
        <p>Wyatt and his team did a wonderful job with our spring clean up and mulching job. Wyatt communicated clearly throughout the process and was always responsive to my questions. His team was respectful and thorough. We will definitely be using them again!</p>
      `,
			author: 'Stephanie B.',
		},
		{
			id: 5,
			rating: 5,
			content: `
        <p>Nice job on our fall-time yard cleaning! Looks wonderful. And, I'm always appreciative of the great communication and follow-through. There are too few contractors and service firms with good follow-up, but you guys are top-notch in that department. Stands you apart from the competition. Keep up the great work!!</p>
      `,
			author: 'Mike N.',
		},
	],
}

function classNames(...classes: (string | boolean | undefined)[]) {
	return classes.filter(Boolean).join(' ')
}

export default function Example() {
	return (
		<div className="bg-white">
			<main className="pt-10 sm:pt-16">
				<nav aria-label="Breadcrumb">
					<ol
						role="list"
						className="mx-auto flex max-w-2xl items-center space-x-2 px-4 sm:px-6 lg:max-w-7xl lg:px-8"
					>
						<li className="text-sm">
							<a
								href={product.href}
								aria-current="page"
								className="font-medium text-gray-500 hover:text-gray-600"
							>
								Business of the Month - July 2024
							</a>
						</li>
					</ol>
				</nav>

				{/* Image gallery */}
				<div className="mx-auto mt-6 max-w-2xl sm:px-6 lg:grid lg:max-w-7xl lg:grid-cols-3 lg:gap-x-8 lg:px-8">
					<div className=" lg:grid lg:grid-cols-1 lg:gap-y-8">
						<div className="aspect-h-2 aspect-w-3 overflow-hidden rounded-lg">
							<img
								src={product.images[1]?.src}
								alt={product.images[1]?.alt}
								className="h-full w-full object-cover object-center"
							/>
						</div>
						<div className="aspect-h-2 aspect-w-3 overflow-hidden rounded-lg">
							<img
								src={product.images[2]?.src}
								alt={product.images[2]?.alt}
								className="h-full w-full object-cover object-center"
							/>
						</div>
					</div>
					<div className="aspect-h-4 aspect-w-3 hidden overflow-hidden rounded-lg lg:block">
						<img
							src={product.images[0]?.src}
							alt={product.images[0]?.alt}
							className="h-full w-full object-cover object-center"
						/>
					</div>
					<div className="hidden lg:grid lg:grid-cols-1 lg:gap-y-8">
						<div className="aspect-h-2 aspect-w-3 overflow-hidden rounded-lg">
							<img
								src={product.images[3]?.src}
								alt={product.images[3]?.alt}
								className="h-full w-full object-cover object-center"
							/>
						</div>
						<div className="aspect-h-2 aspect-w-3 overflow-hidden rounded-lg">
							<img
								src={product.images[4]?.src}
								alt={product.images[4]?.alt}
								className="h-full w-full object-cover object-center"
							/>
						</div>
					</div>
				</div>

				{/* Product info */}
				<div className="mx-auto max-w-2xl px-4 pt-10 sm:px-6 lg:grid lg:max-w-7xl lg:grid-cols-3 lg:grid-rows-[auto,auto,1fr] lg:gap-x-8 lg:px-8 lg:pt-16">
					<div className="lg:col-span-2 lg:border-r lg:border-gray-200 lg:pr-8">
						<h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
							{product.name}
						</h1>
					</div>

					{/* Options */}
					<div className="mt-4 lg:row-span-3 lg:mt-0">
						<form className="mt-10">
							<Link
								to={product.href}
								className="mt-10 flex w-full items-center justify-center rounded-md border border-transparent bg-emerald-600 px-8 py-3 text-base font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
							>
								Visit Website
							</Link>
						</form>
					</div>

					<div className="py-10 lg:col-span-2 lg:col-start-1 lg:border-r lg:border-gray-200 lg:pb-16 lg:pr-8 lg:pt-6">
						<div>
							<h3 className="sr-only">Description</h3>

							<div className="space-y-6">
								<p className="text-base text-gray-900">{product.description}</p>
								<span className="text-gray-600">- Wyatt Conklin</span>
							</div>
						</div>
					</div>

					<div className="lg:col-span-2 lg:border-r lg:border-gray-200 lg:pr-8">
						{/* Reviews */}
						<section
							aria-labelledby="reviews-heading"
							className="border-t border-gray-200 pt-10 lg:pt-16"
						>
							<h2 id="reviews-heading" className="sr-only">
								Reviews
							</h2>

							<div className="space-y-10">
								{reviews.featured.map(review => (
									<div key={review.id} className="flex flex-col sm:flex-row">
										<div className="order-2 mt-6 sm:ml-16 sm:mt-0">
											{/* <h3 className="text-sm font-medium text-gray-900">
												{review.title}
											</h3> */}
											<p className="sr-only">{review.rating} out of 5 stars</p>

											<div
												className="mt-3 space-y-6 text-sm text-gray-600"
												dangerouslySetInnerHTML={{ __html: review.content }}
											/>
										</div>

										<div className="order-1 flex items-center sm:flex-col sm:items-start">
											{/* <img src={review.avatarSrc} alt={`${review.author}.`} className="h-12 w-12 rounded-full" /> */}

											<div className="ml-4 sm:ml-0 sm:mt-4">
												<p className="text-sm font-medium text-gray-900">
													{review.author}
												</p>
												<div className="mt-2 flex items-center">
													{[0, 1, 2, 3, 4].map(rating => (
														<StarIcon
															key={rating}
															className={classNames(
																review.rating > rating
																	? 'text-gray-900'
																	: 'text-gray-200',
																'h-5 w-5 flex-shrink-0',
															)}
															aria-hidden="true"
														/>
													))}
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
							<br />
							<br />
						</section>
					</div>
				</div>
			</main>
			<hr className="py-10" />
		</div>
	)
}
